import React, { useEffect, useCallback, useRef } from 'react';
import { useIonRouter } from '@ionic/react';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { getSignInWidthLinkURL } from './helpers';
import { PublicRoutes } from '../../routes/navigation';
import { useLog } from 'src/shared';
import { useTranslation } from 'react-i18next';

interface AirbridgeDeepLinkData {
  url: string;
  scheme: string;
  host: string;
  path: string;
  queryItems: Record<string, string>;
}

const getSignInWithLinkURL = (queryItems: Record<string, string>) => {
  return `${PublicRoutes.SIGN_IN_BY_LINK_SUCCESS}?apiKey=${queryItems.apiKey}&oobCode=${queryItems.oobCode}&mode=signIn`;
};

const AppUrlListener: React.FC<any> = () => {
  const navigate = useIonRouter();
  const { logError } = useLog();
  const isProcessingRef = useRef(false);
  const { i18n } = useTranslation();

  const handleNavigation = useCallback(
    (isSignIn: boolean, signInUrl: string, fallbackPath: string, fallbackSearch: string = '') => {
      console.log('[AppUrlListener] handleNavigation', {
        fallbackPath,
        fallbackSearch,
        windowLocation: window.location,
      });
      if (isSignIn) {
        console.log('[AppUrlListener] Navigating to sign in');
        navigate.push(signInUrl, 'none', 'replace');
      } else if (window.location.pathname !== fallbackPath || window.location.search !== fallbackSearch) {
        console.log('[AppUrlListener] Navigating to fallback path', fallbackPath + fallbackSearch);
        navigate.push(fallbackPath + fallbackSearch, 'none', 'replace');
      }
      try {
        Browser.close();
      } catch (e) {
        console.log('[AppUrlListener] Failed to close browser');
      }
    },
    [navigate]
  );

  // Handle Capacitor's native app URL open events
  const handleCapacitorUrlOpen = useCallback(
    async (data: { url: string }) => {
      if (isProcessingRef.current) {
        console.log('[AppUrlListener] Skipping Capacitor URL - already processing');
        return;
      }

      isProcessingRef.current = true;
      try {
        console.log('[AppUrlListener] appUrlOpen:', data);
        const url = data.url;
        const deepLinkURL = new URL(url);
        const finalUrl = new URL(
          deepLinkURL.searchParams.get('link') ?? deepLinkURL.searchParams.get('af_dp') ?? deepLinkURL.toString()
        );

        const params = Object.fromEntries(new URLSearchParams(finalUrl.toString().split('/?')[1]).entries()) as {
          token?: string;
          lang?: string;
          match_message: string;
        };

        const lang = deepLinkURL.searchParams.get('lang');
        if (lang) {
          i18n.changeLanguage(lang);
          localStorage.setItem('mindlySelectedLocale', lang);
        }

        if (!params?.match_message) {
          const isSignIn = finalUrl.searchParams.get('mode') === 'signIn';
          const signInUrl = getSignInWidthLinkURL(finalUrl);
          await Preferences.remove({ key: 'DeepLinkData' });
          handleNavigation(isSignIn, signInUrl, finalUrl.pathname, finalUrl.search);
        }
      } catch (e) {
        logError(new Error('[AppUrlListener] Failed to parse Capacitor deep link:'), {
          error: e,
        });
      } finally {
        isProcessingRef.current = false;
      }
    },
    [logError, navigate, handleNavigation]
  );

  // Handle Airbridge deep link events
  const handleAirbridgeDeepLink = useCallback(
    async (event: any) => {
      if (isProcessingRef.current) {
        console.log('[AppUrlListener] Skipping Airbridge deep link - already processing');
        return;
      }

      isProcessingRef.current = true;
      console.log('[AppUrlListener] Airbridge deep link received:', event);

      const data: AirbridgeDeepLinkData = event;

      if (!data?.path) {
        isProcessingRef.current = false;
        console.log('[AppUrlListener] No path found');
        return;
      }

      const lang = data.queryItems?.lang;
      if (lang) {
        i18n.changeLanguage(lang);
        localStorage.setItem('mindlySelectedLocale', lang);
      }

      try {
        // Build query string from queryItems
        const queryString =
          Object.keys(data.queryItems || {}).length > 0 ? '?' + new URLSearchParams(data.queryItems).toString() : '';

        const isSignIn = data.queryItems?.mode === 'signIn';
        const signInUrl = getSignInWithLinkURL(data.queryItems);
        await Preferences.remove({ key: 'DeepLinkData' });
        handleNavigation(isSignIn, signInUrl, data.path, queryString);
      } catch (error) {
        logError(new Error('[AppUrlListener] Failed to handle Airbridge deep link:'), {
          error,
        });
      } finally {
        isProcessingRef.current = false;
      }
    },
    [logError, navigate, handleNavigation]
  );

  // Check for stored deep link data on app start
  const checkStoredDeepLinkData = useCallback(async () => {
    if (isProcessingRef.current || !Capacitor.isNativePlatform()) return;

    console.log('[AppUrlListener] Checking for stored deep link data');
    try {
      await Preferences.configure({
        group: 'MindlyCapacitorStorage',
      });
      const { value } = await Preferences.get({ key: 'DeepLinkData' });
      if (value) {
        console.log('[AppUrlListener] Found stored deep link data:', value);
        const airbridgeData = JSON.parse(value);
        await handleAirbridgeDeepLink(airbridgeData);
      } else {
        console.log('[AppUrlListener] No stored deep link data found');
      }
    } catch (error) {
      logError(new Error('[AppUrlListener] Failed to check stored deep link data:'), {
        error,
      });
    }
  }, [handleAirbridgeDeepLink, logError]);

  useEffect(() => {
    // Check for stored deep link data on component mount
    checkStoredDeepLinkData();

    // Set up listeners
    CapApp.addListener('appUrlOpen', handleCapacitorUrlOpen);

    if (Capacitor.isNativePlatform()) {
      window.addEventListener('airbridgeDeepLink', handleAirbridgeDeepLink as EventListener);
    }

    // Cleanup
    return () => {
      if (Capacitor.isNativePlatform()) {
        window.removeEventListener('airbridgeDeepLink', handleAirbridgeDeepLink as EventListener);
      }
    };
  }, [handleCapacitorUrlOpen, handleAirbridgeDeepLink, checkStoredDeepLinkData]);

  return null;
};

export default AppUrlListener;
